import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CheckCircle,
  ArrowRight,
  Calendar,
  Dumbbell,
  Utensils,
  HeartPulse,
  MessageCircle
} from 'lucide-react'
import Seo from '../components/Seo'
import { coachingPricing } from '../lib/coachingPrograms'
import { EnrollLink } from '../components/ui/EnrollLink'

const InPersonTraining: React.FC = () => {
  const pricing = coachingPricing.strongAgain

  const included = [
    {
      icon: Dumbbell,
      title: 'Customized Strength Training',
      description:
        'Your training is built around your body, goals, experience, schedule, and available equipment.'
    },
    {
      icon: Utensils,
      title: 'Personalized Nutrition Coaching',
      description:
        'Nutrition guidance designed to support your goals without requiring your life to revolve around a diet.'
    },
    {
      icon: MessageCircle,
      title: 'Coaching + Progress Reviews',
      description:
        "Regular check-ins, honest feedback, and adjustments keep your plan responsive to your progress and what's happening in your life."
    },
    {
      icon: HeartPulse,
      title: 'Recovery + Real-Life Strategy',
      description:
        'Recovery, stress management, and consistency strategies help you keep moving forward when life gets busy.'
    }
  ]

  const features = [
    'Fully customized strength-training program',
    'Personalized nutrition coaching',
    'Cardio programming when appropriate',
    'Supplement recommendations when appropriate',
    'Regular coaching consultations',
    'Progress reviews and program adjustments',
    'Recovery and stress-management guidance',
    'Direct coaching support from Cassie',
    'Select in-person coaching available for DMV-area clients'
  ]

  const locations = [
    {
      name: 'Your Own Gym',
      address:
        "Already belong to a gym? I'll build your training around the equipment you have available and what you're working toward.",
      image: '/images/gym-private.webp',
      alt: 'Strength-training equipment at a gym',
      features: [
        'Training designed for your gym',
        'Clear workout instructions',
        'Remote coaching support'
      ]
    },
    {
      name: 'Your Home',
      address:
        "Prefer to train at home? Your plan can be built around your space and equipment, whether you're starting with bodyweight exercises or have a more complete setup.",
      image: '/images/studio-in-home.webp',
      alt: 'Home environment set up for a workout',
      features: [
        'Training for your available equipment',
        'Flexible workout scheduling',
        'Guidance and progress reviews'
      ]
    }
  ]

  return (
    <main className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title="The Strong Again Method™ — Fit by Cassie"
        description="Individualized 1-on-1 strength, nutrition, and habit coaching built around your body, your goals, and your actual life. Founding client investment: $347/month with a 3-month commitment."
        image="/images/cassie-private-training.webp"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-cream px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div
          className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-brand-teal/10 blur-3xl pointer-events-none"
          aria-hidden
        />

        <div
          className="absolute -bottom-40 right-0 w-[420px] h-[420px] rounded-full bg-brand-blush/15 blur-3xl pointer-events-none"
          aria-hidden
        />

        <div className="container-max relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
                Signature 1:1 Coaching
              </span>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-brand-navy mb-6">
                The Strong Again{' '}
                <em className="block italic font-normal text-brand-teal mt-1">
                  Method™
                </em>
              </h1>

              <p className="font-display text-xl sm:text-2xl text-brand-navy/85 leading-relaxed mb-5">
                For women ready to build strength on their own terms.
              </p>

              <p className="text-base sm:text-lg text-brand-navy/70 leading-relaxed max-w-xl mb-8">
                Individualized strength, nutrition, and habit coaching built
                around your body, your goals, your starting point, and your
                actual life.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
                <Link
                  to="/book-consultation"
                  className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-semibold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
                >
                  Schedule Your Complimentary Consultation
                  <Calendar className="ml-2" size={18} />
                </Link>

                <a
                  href="#investment"
                  className="inline-flex items-center justify-center rounded-full border border-brand-navy/25 bg-brand-ivory text-brand-navy font-semibold px-8 py-4 transition-colors hover:bg-white"
                >
                  View Founding Investment
                </a>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {[
                  'Strength',
                  'Nutrition',
                  'Accountability',
                  'Real-Life Consistency'
                ].map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-2 text-sm font-semibold text-brand-navy/70"
                  >
                    <CheckCircle size={15} className="text-brand-teal" />
                    {item}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-[2rem] border border-brand-bronze/20" />

              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg bg-brand-ivory">
                <img
                  src="/images/cassie-private-training.webp"
                  alt="Cassie coaching a private strength-training client"
                  className="w-full h-full object-cover object-[center_22%] aspect-[4/5] sm:aspect-[3/4]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="relative overflow-hidden bg-brand-navy px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center mb-14"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-blush mb-4">
              Coaching Built for Real Life
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-ivory leading-tight mb-6">
              Stop starting over.{' '}
              <em className="italic font-normal text-brand-blush">
                Start becoming strong again.
              </em>
            </h2>

            <p className="text-lg text-brand-cream/65 leading-relaxed">
              This isn't a generic workout plan. The Strong Again Method™ is
              individualized 1:1 coaching designed to help you build strength,
              improve your nutrition, create consistency, and make progress
              without requiring your life to be perfect first.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {included.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="bg-white/10 backdrop-blur-md border border-white/10 rounded-3xl p-7 hover:bg-white/15 transition-colors"
              >
                <div className="w-11 h-11 rounded-full bg-brand-blush/15 flex items-center justify-center mb-5">
                  <item.icon size={21} className="text-brand-blush" />
                </div>

                <h3 className="font-display text-xl font-semibold text-brand-ivory mb-3">
                  {item.title}
                </h3>

                <p className="text-sm text-brand-cream/70 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED + INVESTMENT */}
      <section
        id="investment"
        className="bg-brand-cream px-4 sm:px-6 lg:px-8 py-20 sm:py-24"
      >
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
                What's Included
              </span>

              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy mb-7">
                Coaching that adjusts{' '}
                <em className="italic font-normal text-brand-teal">
                  with you.
                </em>
              </h2>

              <ul className="space-y-4">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle
                      size={18}
                      className="text-brand-teal shrink-0 mt-0.5"
                    />
                    <span className="text-brand-navy/70 leading-relaxed">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-brand-ivory rounded-3xl border border-brand-bronze/25 shadow-soft-lg p-8 sm:p-10 lg:sticky lg:top-32"
            >
              <span className="inline-block rounded-full bg-brand-blush/20 px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand-navy mb-6">
                Founding Client Investment
              </span>

              <h3 className="font-display text-3xl font-semibold text-brand-navy mb-2">
                The Strong Again Method™
              </h3>

              <p className="text-brand-navy/60 mb-8">
                Individualized 1:1 coaching
              </p>

              <div className="space-y-4 mb-8">
                {pricing.map((option) => (
                  <div
                    key={option.label}
                    className="rounded-2xl bg-brand-cream border border-brand-teal/15 p-6"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-brand-teal mb-2">
                      {option.label}
                    </p>

                    <p className="font-display text-4xl font-semibold text-brand-navy">
                      {option.price}
                    </p>

                    {option.detail && (
                      <p className="text-sm text-brand-navy/60 mt-2">
                        {option.detail}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-sm text-brand-navy/65 leading-relaxed mb-7">
                Ready to start? Enroll now, or schedule a complimentary
                consultation first if you'd like to talk through your goals
                and whether this coaching relationship is the right fit.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <EnrollLink
                  packageKey="strongAgain"
                  className="inline-flex flex-1 items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
                />

                <Link
                  to="/book-consultation"
                  className="inline-flex flex-1 items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy font-bold px-7 py-4 transition-all duration-300 hover:bg-brand-navy hover:text-brand-cream"
                >
                  Schedule a Consultation First
                </Link>
              </div>

              <p className="text-center text-xs text-brand-navy/45 mt-4">
                Enroll any time — a consultation is available but never required.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* IN-PERSON OPTIONS */}
      <section className="bg-brand-ivory px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
              DMV Coaching
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy mb-5">
              Online anywhere.{' '}
              <em className="italic font-normal text-brand-teal">
                Select in-person options in the DMV.
              </em>
            </h2>

            <p className="text-lg text-brand-navy/65 max-w-2xl mx-auto leading-relaxed">
              The Strong Again Method™ is built around online coaching. You can
              train at a gym you belong to or in your own space at home, using
              an individualized plan designed around your goals, experience,
              schedule, and available equipment.
            </p>

            <p className="text-lg text-brand-navy/65 max-w-2xl mx-auto leading-relaxed mt-4">
              You'll have clear instructions and coaching support based on your
              program, whether you're following your workouts independently or
              connecting with me by phone or video.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {locations.map((location, index) => (
              <motion.div
                key={location.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.12 }}
                className="relative rounded-3xl overflow-hidden shadow-soft-lg aspect-[5/4] sm:aspect-[4/3]"
              >
                <img
                  src={location.image}
                  alt={location.alt}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/45 to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                  <h3 className="font-display text-2xl font-semibold text-brand-ivory mb-2">
                    {location.name}
                  </h3>

                  <p className="text-brand-cream/80 text-sm mb-4 leading-relaxed">
                    {location.address}
                  </p>

                  <ul className="flex flex-wrap gap-x-5 gap-y-2">
                    {location.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-brand-cream/85 text-xs sm:text-sm"
                      >
                        <CheckCircle
                          className="text-brand-blush shrink-0"
                          size={14}
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 rounded-3xl bg-brand-cream border border-brand-bronze/15 p-7 sm:p-8 text-center max-w-2xl mx-auto"
          >
            <h3 className="font-display text-xl font-semibold text-brand-navy mb-2">
              Prefer in-person support?
            </h3>
            <p className="text-brand-navy/65 leading-relaxed">
              Select in-person training sessions may be available for clients
              in the DC, Maryland, and Virginia area, depending on location
              and scheduling. These sessions are optional and involve an
              additional fee.
            </p>
            <p className="text-brand-navy/65 leading-relaxed mt-2">
              We can discuss availability during your complimentary
              consultation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-cream px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
              Your Next Step
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-6">
              Your starting point. Your goals.{' '}
              <em className="italic font-normal text-brand-teal">
                A plan built around you.
              </em>
            </h2>

            <p className="text-lg text-brand-navy/65 leading-relaxed mb-9 max-w-2xl mx-auto">
              Whether you're just getting started, finding your way back, or
              working toward a more advanced goal, you don't have to figure
              out the next step alone. Let's talk about what you want to
              accomplish and what coaching could look like for you.
            </p>

            <Link
              to="/book-consultation"
              className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-9 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
            >
              Schedule Your Complimentary Consultation
              <ArrowRight className="ml-2" size={20} />
            </Link>

            <p className="text-sm text-brand-navy/50 mt-5">
              Your starting point is information. It isn't a qualification test.
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  )
}

export default InPersonTraining
